import { motion } from "framer-motion";
import React from "react";
import rojanhere from "../../../assets/images/logo.jpeg";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { resume, user } from "../../../static/data";
import { Button } from "../../ButtonElements";
import {
  ButtonCV,
  HeroButtons,
  HeroContainer,
  HeroData,
  HeroEducation,
  HeroGreeting,
  HeroImg,
  HeroName,
  HeroScroll,
  HeroScrollName,
  HeroSectionMain,
  HeroSocial,
  HeroSocialLink,
} from "./HeroSectionElements";

const HeroSection = ({ darkMode }) => {
  const isMobile = useIsMobile();

  const openPdfInNewTab = () => {
    window.open(resume.resume_link, "_blank");
  };

  const downloadPdf = () => {
    const link = document.createElement("a");
    link.href = resume.resume_file;
    link.setAttribute("download", "resume.pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onDownloadPdfClick = () => {
    downloadPdf();
  };

  const socialVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.5,
      },
    },
  };

  const socialItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  };

  return (
    <HeroSectionMain id="home">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-50 to-white opacity-70 z-0"></div>

      {/* Decorative elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ duration: 1, delay: 0.5 }}
        className="absolute top-20 left-10 w-20 h-20 bg-red-100 rounded-full blur-xl z-1"
      ></motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1, delay: 0.7 }}
        className="absolute bottom-40 right-10 w-32 h-32 bg-red-50 rounded-full blur-xl z-1"
      ></motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1, delay: 0.9 }}
        className="absolute top-40 right-1/4 w-16 h-16 bg-orange-50 rounded-full blur-xl z-1"
      ></motion.div>

      <HeroContainer>
        <div className="flex flex-col md:flex-row md:justify-center md:items-center md:gap-12 items-center relative z-10">
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.8,
              type: "spring",
              stiffness: 100,
            }}
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-red-500 to-red-600 rounded-full blur opacity-20"></div>
            <HeroImg
              src={rojanhere}
              alt={user.name}
              className="relative z-10"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`${
              isMobile ? "text-center w-full px-4" : "md:text-left"
            } z-10 relative`}
          >
            <HeroData className={`${isMobile ? "" : "md:items-start"}`}>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <HeroGreeting
                  light={darkMode ? 1 : 0}
                  className="inline-block text-red-600 font-medium"
                >
                  Hello, I'm
                </HeroGreeting>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <HeroName light={darkMode ? 1 : 0} className="mt-2">
                  {user.name}
                </HeroName>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <HeroEducation
                  light={darkMode ? 1 : 0}
                  className="text-gray-700"
                >
                  {user.stack}
                </HeroEducation>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1 }}
              >
                <HeroButtons>
                  <ButtonCV
                    className="hero_ghost flex items-center justify-center gap-2 group"
                    onClick={onDownloadPdfClick}
                  >
                    Download Resume
                    <i className="bx bx-download group-hover:animate-bounce"></i>
                  </ButtonCV>
                  <Button
                    to="about"
                    className="flex items-center justify-center gap-2 group"
                  >
                    About Me
                    <i className="bx bx-user group-hover:animate-pulse"></i>
                  </Button>
                </HeroButtons>
              </motion.div>

              {/* Only show terminal mode announcement on desktop */}
              {/* <motion.div
              //   initial={{ opacity: 0, scale: 0.9 }}
              //   animate={{ opacity: 1, scale: 1 }}
              //   transition={{ duration: 0.6, delay: 1.2 }}
              // >
              //   <HeroButtons className="pt-6">
              //     <div className="relative p-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl border-2 border-yellow-600 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105">
              //       <span className="text-nowrap text-black font-bold text-sm md:text-lg tracking-wide">
              //         Learning WebRTC building{" "}
              //         <a
              //           href="https://nexuschat.rojanacharya.com/"
              //           target="_blank"
              //           rel="noopener noreferrer"
              //           className="text-white underline hover:text-yellow-100 transition"
              //         >
              //           NexusChat
              //         </a>
              //       </span>
              //       <div className="absolute -top-2 -right-2 bg-yellow-200 w-4 h-4 rounded-full animate-ping"></div>
              //       <div className="absolute -bottom-2 -left-2 bg-yellow-200 w-4 h-4 rounded-full animate-ping delay-300"></div>
              //     </div>
              //   </HeroButtons>
              // </motion.div> */}

              {/* Mobile social links for small screens */}
              <motion.div
                variants={socialVariants}
                initial="hidden"
                animate="visible"
                className="flex gap-3 mt-6 md:hidden justify-center relative z-10"
              >
                <motion.a
                  variants={socialItemVariants}
                  href="https://www.linkedin.com/in/racharya404"
                  target="_blank"
                  aria-label="LinkedIn Profile"
                  className="bg-red-900 text-red-400 p-2 rounded-lg text-xl transition-all hover:bg-red-600 hover:text-white hover:-translate-y-1 hover:shadow-md"
                >
                  <i className="bx bxl-linkedin-square"></i>
                </motion.a>
                <motion.a
                  variants={socialItemVariants}
                  href="https://github.com/rjach"
                  target="_blank"
                  aria-label="GitHub Profile"
                  className="bg-red-900 text-red-400 p-2 rounded-lg text-xl transition-all hover:bg-red-600 hover:text-white hover:-translate-y-1 hover:shadow-md"
                >
                  <i className="bx bxl-github"></i>
                </motion.a>
                <motion.a
                  variants={socialItemVariants}
                  href="https://www.reddit.com/user/Official_Rojan"
                  target="_blank"
                  aria-label="Reddit Profile"
                  className="bg-red-900 text-red-400 p-2 rounded-lg text-xl transition-all hover:bg-red-600 hover:text-white hover:-translate-y-1 hover:shadow-md"
                >
                  <i className="bx bxl-reddit"></i>
                </motion.a>
                <motion.a
                  variants={socialItemVariants}
                  href="https://x.com/rojanacharya"
                  target="_blank"
                  aria-label="Twitter Profile"
                  className="bg-red-900 text-red-400 p-2 rounded-lg text-xl transition-all hover:bg-red-600 hover:text-white hover:-translate-y-1 hover:shadow-md"
                >
                  <i className="bx bxl-twitter"></i>
                </motion.a>
              </motion.div>
            </HeroData>
          </motion.div>
        </div>

        <motion.div
          variants={socialVariants}
          initial="hidden"
          animate="visible"
          className="relative z-20"
        >
          <HeroSocial>
            <motion.div variants={socialItemVariants}>
              <HeroSocialLink
                href="https://www.linkedin.com/in/racharya404"
                target={"_blank"}
                aria-label="LinkedIn Profile"
              >
                <i className="bx bxl-linkedin-square"></i>
              </HeroSocialLink>
            </motion.div>
            <motion.div variants={socialItemVariants}>
              <HeroSocialLink
                href="https://github.com/rjach"
                target={"_blank"}
                aria-label="GitHub Profile"
              >
                <i className="bx bxl-github"></i>
              </HeroSocialLink>
            </motion.div>
            <motion.div variants={socialItemVariants}>
              <HeroSocialLink
                href="https://www.reddit.com/user/Official_Rojan"
                target={"_blank"}
                aria-label="Reddit Profile"
              >
                <i className="bx bxl-reddit"></i>
              </HeroSocialLink>
            </motion.div>
            <motion.div variants={socialItemVariants}>
              <HeroSocialLink
                href="https://x.com/rojanacharya"
                target={"_blank"}
                aria-label="Twitter Profile"
              >
                <i className="bx bxl-twitter"></i>
              </HeroSocialLink>
            </motion.div>
          </HeroSocial>
        </motion.div>

        {/* Only show scroll down on desktop */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.5 }}
            className="relative z-20"
          >
            <HeroScroll to="about">
              <div className="animate-bounce">
                <i className="bx bx-mouse home__scroll-icon"></i>
              </div>
              <HeroScrollName>Scroll Down</HeroScrollName>
            </HeroScroll>
          </motion.div>
        )}
      </HeroContainer>
    </HeroSectionMain>
  );
};

export default HeroSection;
